import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'

export default function useBehaviourList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError, openImage } = useCommon()
  const { updateItem, getItemsAggregate } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const behaviours = ref([])
  const collection = 'behaviour'

  const tableColumns = computed(() => {
    return [
      { key: 'name', label: i18n.t('message.tableHeader.behaviours'), sortable: true },
      { key: 'value', label: i18n.t('message.tableHeader.value'), sortable: true },
      { key: 'description', label: i18n.t('label.discussion_question'), sortable: true },
      { key: 'imageKey', label: i18n.t('label.img'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })

  const totalBehaviours = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchBehaviours = async (searchValue = '') => {
    isLoading.value = true

    try {
      const pipeline = [
        { $match: { client_id: oid(clientId), deleted: { $ne: true } } },
        { $lookup: {
          from: 'value',
          localField: 'value_id',
          foreignField: '_id',
          as: 'value',
          pipeline: [
            { $project: { name: 1 } },
            { $addFields: { _id: { $toString: "$_id" } } },
          ],
        }},
        { $addFields: {
          _id: { $toString: "$_id" },
          value: { $arrayElemAt: ["$value", 0] },
        }},
      ]

      const items = await getItemsAggregate({ collection, pipeline })
      if (!items) return

      if (searchValue && searchValue !== searchQuery.value) return

      behaviours.value = searchQuery.value
        ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase()))
        : items
      totalBehaviours.value = behaviours.value.length

    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.behaviour_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchBehaviour = searchVal => {
    isLoading.value = true
    fetchBehaviours(searchVal)
  }

  const deleteBehaviour = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchBehaviours()
      showSuccessMessage(i18n.t('message.behaviour_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (behaviours.value.length ? 1 : 0)
    const to = behaviours.value.length + ((currentPage.value * perPage.value) - perPage.value)
    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalBehaviours.value })
  })

  watch([currentPage, perPage], () => {
    fetchBehaviours()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchBehaviours()
  })

  return {
    fetchBehaviours,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalBehaviours,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    behaviours,
    deleteBehaviour,
    isLoading,
    searchBehaviour,
    openImage,
  }
}
